import React from 'react';
import { SUPPORT_EMAIL } from '../Config/support';
import { isSupportedLocale, SUPPORTED_LOCALES } from '../i18n/config';

interface SEOProps {
    title: string;
    description: string;
    path: string;
}

const SEO: React.FC<SEOProps> = ({ title, description, path }) => {
    const siteUrl = 'https://fileplease.com';
    const normalizedPath =
        path.length > 1 && path.endsWith('/') ? path.slice(0, -1) : path;
    const fullUrl = `${siteUrl}${normalizedPath}`;
    const siteName = 'FilePlease – Photo & File Sharing, No App';
    const titleText =
        title === 'FilePlease' ||
        title === 'Send photos & files instantly between devices'
            ? siteName
            : `${title} | FilePlease`;
    const pathSegments = normalizedPath.split('/').filter(Boolean);
    const locale = isSupportedLocale(pathSegments[0]) ? pathSegments[0] : 'en';
    const pageSuffix = pathSegments.slice(1).join('/');

    React.useEffect(() => {
        const ensureMeta = (id: string, attributes: Record<string, string>) => {
            let element = document.getElementById(id) as HTMLMetaElement | null;
            if (!element) {
                element = document.createElement('meta');
                element.id = id;
                document.head.appendChild(element);
            }

            Object.entries(attributes).forEach(([name, value]) =>
                element?.setAttribute(name, value),
            );
        };

        document.title = titleText;
        document.documentElement.lang = locale;
        document.documentElement.dir = locale === 'ar' ? 'rtl' : 'ltr';

        ensureMeta('fileplease-description', {
            name: 'description',
            content: description,
        });
        ensureMeta('fileplease-og-type', {
            property: 'og:type',
            content: 'website',
        });
        ensureMeta('fileplease-og-title', {
            property: 'og:title',
            content: title,
        });
        ensureMeta('fileplease-og-description', {
            property: 'og:description',
            content: description,
        });
        ensureMeta('fileplease-og-url', {
            property: 'og:url',
            content: fullUrl,
        });
        ensureMeta('fileplease-og-site-name', {
            property: 'og:site_name',
            content: siteName,
        });
        ensureMeta('fileplease-og-image', {
            property: 'og:image',
            content: `${siteUrl}/og_preview.jpg`,
        });
        ensureMeta('fileplease-twitter-card', {
            name: 'twitter:card',
            content: 'summary_large_image',
        });
        ensureMeta('fileplease-twitter-title', {
            name: 'twitter:title',
            content: title,
        });
        ensureMeta('fileplease-twitter-description', {
            name: 'twitter:description',
            content: description,
        });
        ensureMeta('fileplease-twitter-image', {
            name: 'twitter:image',
            content: `${siteUrl}/og_preview.jpg`,
        });

        let canonical = document.querySelector<HTMLLinkElement>(
            'link[rel="canonical"]',
        );
        if (!canonical) {
            canonical = document.createElement('link');
            canonical.rel = 'canonical';
            document.head.appendChild(canonical);
        }
        canonical.href = fullUrl;

        document
            .querySelectorAll('link[data-fileplease-hreflang]')
            .forEach((element) => element.remove());

        SUPPORTED_LOCALES.forEach((alternateLocale) => {
            const alternate = document.createElement('link');
            const alternatePath = pageSuffix
                ? `/${alternateLocale}/${pageSuffix}`
                : `/${alternateLocale}`;
            alternate.rel = 'alternate';
            // Google hreflang uses ISO 639-1; keep /fil URLs and HTML lang.
            alternate.hreflang = alternateLocale === 'fil' ? 'tl' : alternateLocale;
            alternate.href = `${siteUrl}${alternatePath}`;
            alternate.dataset.filepleaseHreflang = 'true';
            document.head.appendChild(alternate);
        });

        const defaultAlternate = document.createElement('link');
        defaultAlternate.rel = 'alternate';
        defaultAlternate.hreflang = 'x-default';
        defaultAlternate.href = `${siteUrl}${pageSuffix ? `/en/${pageSuffix}` : '/en'}`;
        defaultAlternate.dataset.filepleaseHreflang = 'true';
        document.head.appendChild(defaultAlternate);
    }, [description, fullUrl, locale, pageSuffix, title, titleText]);

    return (
        <>
            {/* Schema.org for Google Search */}
            <script type="application/ld+json">
                {JSON.stringify({
                    '@context': 'https://schema.org',
                    '@type': 'SoftwareApplication',
                    name: siteName,
                    operatingSystem: 'Any',
                    applicationCategory: 'UtilitiesApplication',
                    description: description,
                    url: fullUrl,
                    offers: {
                        '@type': 'Offer',
                        price: '0',
                        priceCurrency: 'USD',
                    },
                    provider: {
                        '@type': 'Organization',
                        name: 'FilePlease',
                        url: siteUrl,
                        email: SUPPORT_EMAIL,
                        contactPoint: [
                            {
                                '@type': 'ContactPoint',
                                contactType: 'customer support',
                                email: SUPPORT_EMAIL,
                            },
                        ],
                    },
                })}
            </script>
        </>
    );
};

export default SEO;
